// @ts-nocheck
/**
 * BundleBuilder Web Component
 *
 * Modes:
 *   variety — multi-flavor selector with bundle pricing
 *   single  — standard single-product add to cart
 */
class BundleBuilder extends HTMLElement {
  static PROGRESS_SEGMENT_COUNT = 10;

  connectedCallback() {
    this._bundleMaxQty = this._parseOptionalQty(this.dataset.bundleMaxQty);
    this._bundleMinQty = this._parseMinQty(this.dataset.bundleMinQty);
    this._bundleSellingPlans = this._parseBundleSellingPlans(this.dataset.bundleSellingPlans);
    this._bundleDiscountRules = this._parseBundleDiscountRules(this.dataset.bundleDiscountRules);
    if (this._bundleMaxQty !== null && this._bundleMinQty > this._bundleMaxQty) {
      this._bundleMinQty = this._bundleMaxQty;
    }
    this._legacyDiscountPct = parseFloat(this.dataset.discountPct || '40') || 40;
    this._shippingSavingsCents = Math.max(
      parseInt(this.dataset.bundleShippingSavingsCents || '800', 10) || 800,
      0
    );
    this._moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this._parentTitle = this.dataset.parentTitle || '';
    this._parentImage = this.dataset.parentImage || '';
    this._parentVariantId = this.dataset.parentVariantId || '';
    this._singleVariantId = this.dataset.singleVariantId || null;
    this._singlePrice = parseInt(this.dataset.singlePrice || '0', 10);
    this._singleQty = 1;
    this._lastRenderedBundleTotal = 0;

    // Variety panel refs
    this._footerEl = this.querySelector('[data-bb-footer]');
    this._footerTitleEl = this.querySelector('[data-bb-footer-title]');
    this._footerBadgeEl = this.querySelector('[data-bb-footer-badge]');
    this._footerSubtitleEl = this.querySelector('[data-bb-footer-subtitle]');
    this._footerFsMessageEl = this.querySelector('[data-bb-footer-fs-message]');
    this._footerNextMessageEl = this.querySelector('[data-bb-footer-next-message]');
    this._footerPricesEl = this.querySelector('[data-bb-footer-prices]');
    this._savingsTagEl = this.querySelector('[data-bb-savings-tag]');
    this._discountedPriceEl = this.querySelector('[data-bb-discounted-price]');
    this._originalPriceEl = this.querySelector('[data-bb-original-price]');
    this._unitPriceEl = this.querySelector('[data-bb-unit-price]');
    this._savingsTextEl = this.querySelector('[data-bb-savings-text]');
    this._addToCartBtn = this.querySelector('[data-bb-add-to-cart]');
    this._bundleSubscriptionWrap = this.querySelector('[data-bb-subscribe-wrap]');
    this._bundleSubscriptionSelect = this.querySelector('[data-bb-subscribe-plan]');
    this._addSubscriptionBtn = this.querySelector('[data-bb-add-subscription]');
    this._selectedLabelEl = this.querySelector('[data-bb-selected-label]');
    this._selectedCountEl = this.querySelector('[data-bb-selected-count]');
    this._unlockMessageEl = this.querySelector('[data-bb-unlock-message]');
    this._unlockTagEl = this.querySelector('[data-bb-unlock-tag]');
    this._freeShippingTagEl = this.querySelector('[data-bb-free-shipping-tag]');
    this._freeShippingMessageEl = this.querySelector('[data-bb-free-shipping-message]');
    this._progressEl = this.querySelector('[data-bb-progress]');

    // Single panel refs
    this._singleQtyEl = this.querySelector('[data-bb-single-qty]');
    this._singlePriceDisplay = this.querySelector('[data-bb-single-price-display]');
    this._singleAddBtn = this.querySelector('[data-bb-single-add]');
    this._singleMinusBtn = this.querySelector('[data-bb-single-minus]');
    this._singlePlusBtn = this.querySelector('[data-bb-single-plus]');

    // Flavor items: variantId → { qty, price }
    this._items = new Map();

    // ── Wire pack tab buttons ──────────────────────────────
    this.querySelectorAll('[data-bb-pack-tab]').forEach((btn) => {
      btn.addEventListener('click', () => this._switchMode(btn.dataset.bbPackTab));
    });

    // ── Wire variety flavor cards ──────────────────────────
    this.querySelectorAll('.bb-card').forEach((card) => {
      this._registerVarietyCard(card);
    });

    // ── Wire variety CTA ───────────────────────────────────
    if (this._addToCartBtn) {
      this._addToCartBtn.addEventListener('click', () => this._addBundleToCart());
    }
    if (this._addSubscriptionBtn) {
      this._addSubscriptionBtn.addEventListener('click', () => this._addBundleToCart(true));
    }

    // ── Wire single mode ───────────────────────────────────
    if (this._singleMinusBtn) {
      this._singleMinusBtn.addEventListener('click', () => {
        if (this._singleQty > 1) {
          this._singleQty--;
          this._renderSingle();
        }
      });
    }
    if (this._singlePlusBtn) {
      this._singlePlusBtn.addEventListener('click', () => {
        this._singleQty++;
        this._renderSingle();
      });
    }
    if (this._singleAddBtn) {
      this._singleAddBtn.addEventListener('click', () => this._addSingleToCart());
    }

    // Initial render
    this._renderBundleSubscriptionOptions();
    this._renderVariety();
    this._renderSingle();
  }

  _parseOptionalQty(value) {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed <= 0) return null;
    return parsed;
  }

  _parseMinQty(value) {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed <= 0) return 0;
    return parsed;
  }

  _parseBundleSellingPlans(value) {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((plan) => ({
          id: parseInt(plan?.id, 10),
          name: String(plan?.name || ''),
        }))
        .filter((plan) => Number.isInteger(plan.id) && plan.id > 0 && plan.name);
    } catch (error) {
      console.error('[BundleBuilder] Invalid selling plan payload', error);
      return [];
    }
  }

  _parseBundleDiscountRules(value) {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((rule) => {
          const normalizedType = this._normalizeRuleType(rule?.type);
          const minimum = parseInt(rule?.minimum, 10);
          const maximumRaw = rule?.maximum;
          const hasMaximumValue = maximumRaw !== null && maximumRaw !== undefined && `${maximumRaw}`.trim() !== '';
          const parsedMaximum = hasMaximumValue ? parseInt(maximumRaw, 10) : null;
          const maximum = normalizedType === 'fixed' || !Number.isInteger(parsedMaximum) || parsedMaximum <= 0
            ? null
            : parsedMaximum;
          const normalizedDiscountType = this._normalizeDiscountType(rule?.discount_type);
          const valueNumber = parseFloat(rule?.value);

          if (!Number.isInteger(minimum) || minimum < 0) return null;
          if (maximum !== null && maximum < minimum) return null;

          return {
            type: normalizedType,
            minimum,
            maximum,
            discountType: normalizedDiscountType,
            value: Number.isFinite(valueNumber) ? valueNumber : 0,
            freeShipping: this._toBoolean(rule?.free_shipping),
            label: String(rule?.label || '').trim(),
          };
        })
        .filter((rule) => rule !== null)
        .sort((a, b) => {
          if (a.minimum !== b.minimum) return a.minimum - b.minimum;
          const aMax = a.maximum === null ? Number.POSITIVE_INFINITY : a.maximum;
          const bMax = b.maximum === null ? Number.POSITIVE_INFINITY : b.maximum;
          return aMax - bMax;
        });
    } catch (error) {
      console.error('[BundleBuilder] Invalid discount rules payload', error);
      return [];
    }
  }

  _normalizeRuleType(type) {
    const normalized = String(type || '').trim().toLowerCase();
    if (normalized === 'fixed quantity' || normalized === 'fixed_quantity' || normalized === 'fixed') {
      return 'fixed';
    }
    return 'range';
  }

  _normalizeDiscountType(type) {
    const normalized = String(type || '').trim().toLowerCase();
    if (normalized === 'fixed amount' || normalized === 'fixed_amount' || normalized === 'fixed') {
      return 'fixed_amount';
    }
    return 'percentage';
  }

  _toBoolean(value) {
    if (typeof value === 'boolean') return value;
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }

  _getLegacyTierDiscountPct(totalQty) {
    if (totalQty >= 10) return 25;
    if (totalQty >= 8) return 18;
    if (totalQty >= 6) return 12;
    if (totalQty >= 5) return 10;
    if (totalQty >= 3) return 5;
    if (totalQty <= 0) return 0;
    return this._legacyDiscountPct > 0 ? this._legacyDiscountPct : 0;
  }

  _getDiscountCopy(rule) {
    if (!rule) return '';

    if (rule.discountType === 'fixed_amount') {
      const cents = Math.max(Math.round(rule.value * 100), 0);
      return cents > 0 ? `${this._formatMoney(cents)} off` : '';
    }

    return rule.value > 0 ? `${Math.round(rule.value)}% off` : '';
  }

  _formatRuleBenefitLabel(rule) {
    if (!rule) return '';

    return this._getDiscountCopy(rule) || '';
  }

  _formatRuleLabel(rule) {
    if (!rule) return '';

    const tierLabel = String(rule.label || '').trim();
    if (tierLabel) return tierLabel;

    const discountCopy = this._getDiscountCopy(rule);
    return discountCopy || '';
  }

  _formatRuleLabelNoShipping(rule) {
    return this._formatRuleLabel(rule);
  }

  _sortRulesByMinimumDesc(a, b) {
    if (a.minimum !== b.minimum) return b.minimum - a.minimum;
    const aMax = a.maximum === null ? Number.POSITIVE_INFINITY : a.maximum;
    const bMax = b.maximum === null ? Number.POSITIVE_INFINITY : b.maximum;
    return aMax - bMax;
  }

  _sortRulesByMinimumAsc(a, b) {
    if (a.minimum !== b.minimum) return a.minimum - b.minimum;
    const aMax = a.maximum === null ? Number.POSITIVE_INFINITY : a.maximum;
    const bMax = b.maximum === null ? Number.POSITIVE_INFINITY : b.maximum;
    return aMax - bMax;
  }

  _ruleMatchesQty(rule, totalQty) {
    if (!rule) return false;
    const effectiveMaximum = rule.maximum === null ? Number.POSITIVE_INFINITY : rule.maximum;

    if (rule.type === 'fixed') {
      return totalQty === rule.minimum;
    }

    return totalQty >= rule.minimum && totalQty <= effectiveMaximum;
  }

  _getMatchingDiscountRule(totalQty) {
    if (!Array.isArray(this._bundleDiscountRules) || this._bundleDiscountRules.length === 0) {
      return null;
    }

    const matchingRules = this._bundleDiscountRules.filter((rule) => this._ruleMatchesQty(rule, totalQty));
    if (!matchingRules.length) {
      // For fixed-step tiers (e.g. 6, 8, 10), keep the last unlocked tier active
      // until the next tier is reached.
      const unlockedRules = this._bundleDiscountRules.filter((rule) => rule.minimum <= totalQty);
      if (!unlockedRules.length) return null;

      return unlockedRules.sort((a, b) => this._sortRulesByMinimumDesc(a, b))[0];
    }

    return matchingRules.sort((a, b) => this._sortRulesByMinimumDesc(a, b))[0];
  }

  _getNextDiscountRule(totalQty) {
    if (!Array.isArray(this._bundleDiscountRules) || this._bundleDiscountRules.length === 0) {
      return null;
    }

    const nextRules = this._bundleDiscountRules.filter((rule) => {
      if (rule.type === 'fixed') return totalQty < rule.minimum;
      const upperBound = rule.maximum === null ? Number.POSITIVE_INFINITY : rule.maximum;
      if (totalQty < rule.minimum) return true;
      return totalQty >= rule.minimum && totalQty < upperBound;
    });

    if (!nextRules.length) return null;

    return nextRules.sort((a, b) => this._sortRulesByMinimumAsc(a, b))[0];
  }

  _renderBundleSubscriptionOptions() {
    const hasPlans = Array.isArray(this._bundleSellingPlans) && this._bundleSellingPlans.length > 0;

    if (this._bundleSubscriptionWrap) {
      this._bundleSubscriptionWrap.hidden = !hasPlans;
    }

    if (!this._bundleSubscriptionSelect) return;

    this._bundleSubscriptionSelect.innerHTML = '';

    if (!hasPlans) {
      this._bundleSubscriptionSelect.disabled = true;
      return;
    }

    const fragment = document.createDocumentFragment();
    this._bundleSellingPlans.forEach((plan, index) => {
      const option = document.createElement('option');
      option.value = String(plan.id);
      option.textContent = plan.name;
      if (index === 0) option.selected = true;
      fragment.appendChild(option);
    });

    this._bundleSubscriptionSelect.appendChild(fragment);
    this._bundleSubscriptionSelect.disabled = false;
  }

  _getSelectedBundleSellingPlanId() {
    if (!this._bundleSubscriptionSelect) return null;

    const selectedPlanId = parseInt(this._bundleSubscriptionSelect.value || '0', 10);
    if (!Number.isInteger(selectedPlanId) || selectedPlanId <= 0) return null;

    return selectedPlanId;
  }

  _getProgressTargetQty() {
    if (this._bundleMinQty > 0) return this._bundleMinQty;
    if (this._bundleMaxQty !== null && this._bundleMaxQty > 0) return this._bundleMaxQty;
    return 0;
  }

  _getUnlockQty() {
    const firstRuleWithMinimum = Array.isArray(this._bundleDiscountRules)
      ? this._bundleDiscountRules.find((rule) => Number.isInteger(rule.minimum) && rule.minimum > 0)
      : null;

    if (firstRuleWithMinimum) {
      return firstRuleWithMinimum.minimum;
    }

    if (this._bundleMinQty > 0) return this._bundleMinQty;

    return this._getProgressTargetQty();
  }

  _renderFooterNextTierMessage(total) {
    if (!this._footerFsMessageEl) return;
    if (!Array.isArray(this._bundleDiscountRules) || this._bundleDiscountRules.length === 0) {
      this._footerFsMessageEl.classList.add('bb-status__tag--hidden');
      return;
    }

    const fsThr = this._getFreeShippingThreshold();
    const fsRule = fsThr !== null
      ? this._bundleDiscountRules.find((r) => r.freeShipping && r.minimum === fsThr)
      : null;
    const fsReached = fsThr !== null && total >= fsThr;

    if (fsRule && !fsReached) {
      const n = fsThr - total;
      let label = '';
      if (fsRule.discountType === 'fixed_amount') {
        const cents = Math.round(fsRule.value * 100);
        if (cents > 0) label = `${this._formatMoney(cents)} OFF`;
      } else if (fsRule.value > 0) {
        label = `${Math.round(fsRule.value)}% OFF`;
      }
      if (label) {
        this._footerFsMessageEl.textContent = `ADD ${n} ${n === 1 ? 'CAN' : 'CANS'} AND UNLOCK ${label}`;
        this._footerFsMessageEl.classList.remove('bb-status__tag--hidden');
      } else {
        this._footerFsMessageEl.classList.add('bb-status__tag--hidden');
      }

      // Line 2 (next-message): only show once first threshold is reached
      if (this._footerNextMessageEl) {
        const unlockQty = this._getUnlockQty();
        const bundleUnlocked = unlockQty > 0 ? total >= unlockQty : total > 0;
        if (bundleUnlocked) {
          const nextIncremental = this._bundleDiscountRules.find((r) => r.minimum > total && r.minimum !== fsThr);
          if (nextIncremental && nextIncremental.minimum < fsThr) {
            const ni = nextIncremental.minimum - total;
            const nextLabel = nextIncremental.discountType === 'fixed_amount'
              ? `${this._formatMoney(Math.round(nextIncremental.value * 100))} OFF`
              : nextIncremental.value > 0 ? `${Math.round(nextIncremental.value)}% OFF` : '';
            if (nextLabel) {
              this._footerNextMessageEl.textContent = `ADD ${ni} ${ni === 1 ? 'CAN' : 'CANS'} AND UNLOCK ${nextLabel}`;
              this._footerNextMessageEl.classList.remove('bb-status__tag--hidden');
            } else {
              this._footerNextMessageEl.classList.add('bb-status__tag--hidden');
            }
          } else {
            this._footerNextMessageEl.classList.add('bb-status__tag--hidden');
          }
        } else {
          this._footerNextMessageEl.classList.add('bb-status__tag--hidden');
        }
      }
      return;
    }

    // Free shipping reached — show max benefit in fs line
    const lastRule = this._bundleDiscountRules[this._bundleDiscountRules.length - 1];
    const maxPct = lastRule && lastRule.value > 0 ? `${Math.round(lastRule.value)}% OFF` : '';
    const maxLabel = maxPct ? `${maxPct} UNLOCKED` : '';
    if (maxLabel) {
      this._footerFsMessageEl.textContent = maxLabel;
      this._footerFsMessageEl.classList.remove('bb-status__tag--hidden');
    } else {
      this._footerFsMessageEl.classList.add('bb-status__tag--hidden');
    }

    // Next incremental tier (always separate line)
    const nextRule = this._bundleDiscountRules.find((r) => r.minimum > total);
    if (this._footerNextMessageEl) {
      if (nextRule) {
        const n = nextRule.minimum - total;
        const nextLabel = nextRule.discountType === 'fixed_amount'
          ? `${this._formatMoney(Math.round(nextRule.value * 100))} OFF`
          : nextRule.value > 0 ? `${Math.round(nextRule.value)}% OFF` : '';
        if (nextLabel) {
          this._footerNextMessageEl.textContent = `ADD ${n} ${n === 1 ? 'CAN' : 'CANS'} AND UNLOCK ${nextLabel}`;
          this._footerNextMessageEl.classList.remove('bb-status__tag--hidden');
        } else {
          this._footerNextMessageEl.classList.add('bb-status__tag--hidden');
        }
      } else {
        this._footerNextMessageEl.classList.add('bb-status__tag--hidden');
      }
    }
  }

  _getFreeShippingThreshold() {
    if (!Array.isArray(this._bundleDiscountRules)) return null;
    const rule = this._bundleDiscountRules.find((r) => r.freeShipping && r.minimum > 0);
    return rule ? rule.minimum : null;
  }

  _renderStatusAndProgress(total) {
    const minimumQty = this._getUnlockQty();
    const freeShippingThreshold = this._getFreeShippingThreshold();
    const freeShippingReached = freeShippingThreshold !== null && total >= freeShippingThreshold;
    const previousTotal = Number.isInteger(this._lastRenderedBundleTotal)
      ? this._lastRenderedBundleTotal
      : 0;

    if (this._selectedLabelEl) {
      this._selectedLabelEl.innerHTML = `SELECTED <span data-bb-selected-count>${total}</span>`;
      this._selectedCountEl = this._selectedLabelEl.querySelector('[data-bb-selected-count]');
    }

    if (this._selectedCountEl) {
      this._selectedCountEl.textContent = String(total);
    }

    const unlockQty = minimumQty;
    const bundleUnlocked = unlockQty > 0 ? total >= unlockQty : total > 0;

    const fsThr = this._getFreeShippingThreshold();
    const nextRule = Array.isArray(this._bundleDiscountRules)
      ? this._bundleDiscountRules.find((r) => r.minimum > total)
      : null;

    let tagMessage = '';
    let hideTag = false;
    let isMaxUnlocked = false;

    if (!bundleUnlocked) {
      tagMessage = unlockQty <= 0
        ? 'SELECT FLAVORS TO BUILD YOUR BUNDLE'
        : `ADD ${unlockQty - total} TO UNLOCK BUNDLE PRICE`;
    } else if (nextRule) {
      const n = nextRule.minimum - total;
      const benefit = nextRule.discountType === 'fixed_amount'
        ? `${this._formatMoney(Math.round(nextRule.value * 100))} OFF`
        : nextRule.value > 0 ? `${Math.round(nextRule.value)}% OFF` : '';
      tagMessage = benefit ? `ADD ${n} ${n === 1 ? 'CAN' : 'CANS'} AND UNLOCK ${benefit}` : '';
      if (!tagMessage) { tagMessage = 'MAX SAVINGS UNLOCKED - BEST PRICE PER PACK'; isMaxUnlocked = true; }
    } else {
      tagMessage = 'MAX SAVINGS UNLOCKED - BEST PRICE PER PACK';
      isMaxUnlocked = true;
    }

    if (this._unlockMessageEl) {
      this._unlockMessageEl.textContent = tagMessage;
    }

    if (this._unlockTagEl) {
      this._unlockTagEl.classList.toggle('bb-status__tag--hidden', hideTag);
      this._unlockTagEl.classList.toggle('bb-status__tag--max-unlocked', isMaxUnlocked);
    }

    if (this._freeShippingTagEl) {
      const freeShippingRule = freeShippingThreshold !== null
        ? this._bundleDiscountRules.find((r) => r.freeShipping && r.minimum === freeShippingThreshold)
        : null;
      const unlockTagAlreadyShowingFs = !hideTag && nextRule && nextRule.freeShipping;
      if (freeShippingRule && !freeShippingReached && !unlockTagAlreadyShowingFs) {
        const n = freeShippingRule.minimum - total;
        const label = this._formatRuleLabel(freeShippingRule);
        if (this._freeShippingMessageEl) {
          this._freeShippingMessageEl.textContent = `ADD ${n} MORE ${n === 1 ? 'CAN' : 'CANS'} AND UNLOCK ${label}!`;
        }
        this._freeShippingTagEl.classList.remove('bb-status__tag--hidden');
      } else {
        this._freeShippingTagEl.classList.add('bb-status__tag--hidden');
      }
    }

    if (!this._progressEl) return;

    this._progressEl.classList.toggle('bb-progress--min-reached', minimumQty > 0 && total >= minimumQty);
    this._progressEl.classList.toggle('bb-progress--free-shipping-reached', freeShippingReached);

    const renderCount = BundleBuilder.PROGRESS_SEGMENT_COUNT;
    const filledCount = Math.min(Math.max(total, 0), renderCount);
    const fillPercent = (filledCount / renderCount) * 100;

    // Build tier list: all discount rules → { label, minimum, percent }
    const tiers = [];
    if (Array.isArray(this._bundleDiscountRules)) {
      this._bundleDiscountRules.forEach((rule) => {
        if (!rule || rule.minimum <= 0) return;
        const isHigherFsRule = rule.freeShipping && freeShippingThreshold !== null && rule.minimum > freeShippingThreshold;
        const label = isHigherFsRule ? this._formatRuleLabelNoShipping(rule) : this._formatRuleLabel(rule);
        if (!label) return;
        const clamped = Math.min(Math.max(rule.minimum, 1), renderCount);
        tiers.push({
          label,
          minimum: rule.minimum,
          percent: (clamped / renderCount) * 100,
        });
      });
    }

    // Build DOM only on first render or when tiers change
    const tiersSignature = tiers.map((t) => `${t.minimum}|${t.label}`).join(',');
    if (this._progressEl.dataset.tiersSignature !== tiersSignature) {
      this._progressEl.dataset.tiersSignature = tiersSignature;
      this._progressEl.innerHTML = '';

      const track = document.createElement('div');
      track.className = 'bb-progress__track';

      const fill = document.createElement('div');
      fill.className = 'bb-progress__fill';
      track.appendChild(fill);

      this._progressEl.appendChild(track);

      const markersLayer = document.createElement('div');
      markersLayer.className = 'bb-progress__markers';

      tiers.forEach((tier) => {
        const marker = document.createElement('div');
        marker.className = 'bb-progress__marker';
        marker.style.left = `${tier.percent}%`;
        marker.dataset.minimum = String(tier.minimum);

        const dot = document.createElement('span');
        dot.className = 'bb-progress__marker-dot';
        marker.appendChild(dot);

        const tag = document.createElement('span');
        tag.className = 'bb-progress__marker-tag';
        tag.setAttribute('aria-hidden', 'true');
        tag.textContent = tier.label;
        marker.appendChild(tag);

        markersLayer.appendChild(marker);
      });

      this._progressEl.appendChild(markersLayer);

      this._progressFillEl = fill;
      this._progressTrackEl = track;
      this._progressMarkersLayerEl = markersLayer;

      requestAnimationFrame(() => this._resolveTierMarkerOverlap());
    }

    if (this._progressFillEl) {
      this._progressFillEl.style.width = `${fillPercent}%`;
    }

    if (this._progressMarkersLayerEl) {
      this._progressMarkersLayerEl.querySelectorAll('.bb-progress__marker').forEach((marker) => {
        const min = parseInt(marker.dataset.minimum || '0', 10);
        marker.classList.toggle('bb-progress__marker--reached', total >= min);
      });
    }

    this._lastRenderedBundleTotal = total;
  }

  _resolveTierMarkerOverlap() {
    if (!this._progressMarkersLayerEl) return;
    const markers = Array.from(this._progressMarkersLayerEl.querySelectorAll('.bb-progress__marker'));
    markers.forEach((marker, index) => {
      // Keep Most Popular (index 2) and the last marker (Max Savings) above to reduce overlap.
      const isMostPopularMarker = index === 2;
      const isLastMarker = index === markers.length - 1;
      marker.classList.toggle('bb-progress__marker--above', isMostPopularMarker || isLastMarker);
    });
  }

  // ── Mode switch ────────────────────────────────────────────

  _switchMode(mode) {
    this.querySelectorAll('[data-bb-pack-tab]').forEach((btn) => {
      btn.classList.toggle('bb-pack-tab--active', btn.dataset.bbPackTab === mode);
    });
    this.querySelectorAll('[data-bb-panel]').forEach((panel) => {
      panel.classList.toggle('bb-panel--hidden', panel.dataset.bbPanel !== mode);
    });
  }

  // ── Helpers ────────────────────────────────────────────────

  _registerVarietyCard(card) {
    const variantId = card.dataset.variantId;
    if (!variantId) return;

    const price = parseInt(card.dataset.price || '0', 10);
    this._items.set(variantId, { qty: 0, price });

    const minusBtn = card.querySelector('[data-bb-minus]');
    const plusBtn = card.querySelector('[data-bb-plus]');
    if (minusBtn) {
      minusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._changeQty(variantId, -1);
      });
    }
    if (plusBtn) {
      plusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._changeQty(variantId, 1);
      });
    }
  }

  _createVarietyCard(product) {
    const card = document.createElement('div');
    card.className = 'bb-card';
    card.dataset.variantId = String(product.variantId || '');
    card.dataset.price = String(product.price || 0);
    card.dataset.selected = 'false';
    if (product.cardColor) {
      card.style.setProperty('--bb-card-color', String(product.cardColor));
    }

    const imgWrap = document.createElement('div');
    imgWrap.className = 'bb-card__img-wrap';

    if (product.image) {
      const img = document.createElement('img');
      img.className = 'bb-card__img';
      img.loading = 'lazy';
      img.src = String(product.image);
      img.alt = String(product.title || 'Flavor image');
      img.width = 80;
      img.height = 80;
      imgWrap.appendChild(img);
    }

    const name = document.createElement('p');
    name.className = 'bb-card__name';
    name.textContent = String(product.title || 'Flavor');

    const qtyRow = document.createElement('div');
    qtyRow.className = 'bb-card__qty-row';

    const minus = document.createElement('button');
    minus.className = 'bb-qty-btn';
    minus.type = 'button';
    minus.disabled = true;
    minus.setAttribute('aria-label', 'Decrease');
    minus.setAttribute('data-bb-minus', '');
    minus.textContent = '−';

    const qty = document.createElement('span');
    qty.className = 'bb-card__qty';
    qty.setAttribute('data-bb-qty', '');
    qty.textContent = '0';

    const plus = document.createElement('button');
    plus.className = 'bb-qty-btn';
    plus.type = 'button';
    plus.setAttribute('aria-label', 'Increase');
    plus.setAttribute('data-bb-plus', '');
    plus.textContent = '+';

    qtyRow.append(minus, qty, plus);
    card.append(imgWrap, name, qtyRow);
    return card;
  }

  updateVariety({ bundleMaxQty, bundleMinQty, bundleSellingPlans, bundleDiscountRules, products }) {
    const grid = this.querySelector('.bb-grid');
    if (!grid) return;

    const nextMax = this._parseOptionalQty(bundleMaxQty);
    const nextMin = this._parseMinQty(bundleMinQty);

    this._bundleMaxQty = nextMax;
    this._bundleMinQty = nextMin;
    this._bundleSellingPlans = Array.isArray(bundleSellingPlans) ? bundleSellingPlans : [];
    if (Array.isArray(bundleDiscountRules)) {
      this._bundleDiscountRules = bundleDiscountRules;
    }

    if (this._bundleMaxQty !== null && this._bundleMinQty > this._bundleMaxQty) {
      this._bundleMinQty = this._bundleMaxQty;
    }

    this._items.clear();
    grid.innerHTML = '';

    if (Array.isArray(products)) {
      products.forEach((product) => {
        if (!product || !product.variantId) return;
        const card = this._createVarietyCard(product);
        grid.appendChild(card);
        this._registerVarietyCard(card);
      });
    }

    this._renderBundleSubscriptionOptions();
    this._renderVariety();
  }

  get _totalQty() {
    let n = 0;
    this._items.forEach(({ qty }) => (n += qty));
    return n;
  }

  _formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this._moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
  }

  _getTierDiscountPct(totalQty) {
    const activeRule = this._getMatchingDiscountRule(totalQty);
    if (activeRule && activeRule.discountType === 'percentage') {
      return Math.max(Math.round(activeRule.value), 0);
    }

    if (Array.isArray(this._bundleDiscountRules) && this._bundleDiscountRules.length > 0) {
      return 0;
    }

    return this._getLegacyTierDiscountPct(totalQty);
  }

  _getBundlePricing(totalQty) {
    let originalCents = 0;
    let discountedCents = 0;
    this._items.forEach(({ qty, price }) => {
      const safeQty = Number.isInteger(qty) && qty > 0 ? qty : 0;
      const safePrice = Number.isInteger(price) && price > 0 ? price : 0;
      originalCents += safePrice * safeQty;

      if (safeQty > 0 && safePrice > 0) {
        discountedCents += safePrice * safeQty;
      }
    });

    const unlockQty = this._getUnlockQty();
    const unlocked = unlockQty > 0 ? totalQty >= unlockQty : totalQty > 0;
    const activeRule = unlocked ? this._getMatchingDiscountRule(totalQty) : null;
    const fallbackDiscountPct = unlocked && !activeRule ? this._getLegacyTierDiscountPct(totalQty) : 0;

    if (activeRule) {
      if (activeRule.discountType === 'fixed_amount') {
        const fixedDiscountCents = Math.max(Math.round(activeRule.value * 100), 0);
        discountedCents = Math.max(originalCents - fixedDiscountCents, 0);
      } else {
        const percentage = Math.max(activeRule.value, 0);
        discountedCents = Math.max(
          Math.round((originalCents * (100 - percentage)) / 100),
          0
        );
      }
    } else if (fallbackDiscountPct > 0) {
      discountedCents = 0;
      this._items.forEach(({ qty, price }) => {
        const safeQty = Number.isInteger(qty) && qty > 0 ? qty : 0;
        const safePrice = Number.isInteger(price) && price > 0 ? price : 0;
        if (safeQty === 0 || safePrice === 0) return;

        const lineOriginalCents = safePrice * safeQty;
        const lineDiscountedCents = Math.max(
          Math.round((lineOriginalCents * (100 - fallbackDiscountPct)) / 100),
          0
        );
        discountedCents += lineDiscountedCents;
      });
    }

    const savingsCents = Math.max(originalCents - discountedCents, 0);
    const activeDiscountPct = activeRule
      ? (activeRule.discountType === 'percentage'
        ? Math.max(Math.round(activeRule.value), 0)
        : (originalCents > 0
          ? Math.max(Math.round((savingsCents * 100) / originalCents), 0)
          : 0))
      : fallbackDiscountPct;

    return {
      originalCents,
      discountedCents,
      savingsCents,
      discountPct: activeDiscountPct,
      activeRule,
      unlocked,
    };
  }

  // ── Variety: change qty for one flavor ────────────────────

  _changeQty(variantId, delta) {
    const item = this._items.get(variantId);
    if (!item) return;
    const newQty = item.qty + delta;
    if (newQty < 0) return;

    item.qty = newQty;

    const card = /** @type {HTMLElement|null} */ (
      this.querySelector(`.bb-card[data-variant-id="${variantId}"]`)
    );
    if (!card) return;
    card.querySelector('[data-bb-qty]').textContent = newQty;
    const minusBtn = /** @type {HTMLButtonElement|null} */ (card.querySelector('[data-bb-minus]'));
    if (minusBtn) minusBtn.disabled = newQty === 0;
    card.dataset.selected = newQty > 0 ? 'true' : 'false';

    this._renderVariety();
  }

  // ── Variety: render pricing + CTA ──────────────────────────

  _renderVariety() {
    const total = this._totalQty;
    const targetQty = this._getProgressTargetQty();
    const minRequired = this._getUnlockQty();
    const maxAllowed = null;
    const unlockQty = minRequired > 0 ? minRequired : targetQty;
    const remainingMin = Math.max(minRequired - total, 0);
    const reachedMin = total >= minRequired;
    const reachedMax = false;
    const reachedTargetQty = unlockQty > 0 && total >= unlockQty;
    const hasItems = total > 0;
    const isReady = hasItems && reachedMin;
    const hasSubscriptionPlans = Array.isArray(this._bundleSellingPlans) && this._bundleSellingPlans.length > 0;

    this._renderStatusAndProgress(total);

    // Plus buttons: no hard cap; progress visual limit is handled separately.
    this.querySelectorAll('.bb-card').forEach((card) => {
      const plusBtn = /** @type {HTMLButtonElement|null} */ (card.querySelector('[data-bb-plus]'));
      if (plusBtn) plusBtn.disabled = false;
    });

    // Pricing (show once any items selected)
    const pricing = this._getBundlePricing(total);
    const { originalCents, discountedCents, savingsCents, activeRule } = pricing;

    document.dispatchEvent(
      new CustomEvent('pdp:bundle-state-change', {
        detail: {
          source: 'bundle-builder',
          quantity: total,
          minQty: minRequired,
          ready: isReady,
          totalCents: discountedCents,
        },
      })
    );

    if (this._footerEl) {
      if (hasItems) {
        this._footerEl.classList.add('bb-footer--visible');

        const hasFreeShipping = Boolean(activeRule?.freeShipping);
        const totalSavingsCents = hasFreeShipping
          ? savingsCents + this._shippingSavingsCents
          : savingsCents;

        const unitPriceCents = total > 0 ? Math.round(discountedCents / total) : 0;

        if (this._originalPriceEl) this._originalPriceEl.textContent = this._formatMoney(originalCents);
        if (this._discountedPriceEl) this._discountedPriceEl.textContent = this._formatMoney(discountedCents);
        if (this._unitPriceEl) {
          if (total > 1) {
            this._unitPriceEl.textContent = `${this._formatMoney(unitPriceCents)} / Unit`;
            this._unitPriceEl.hidden = false;
          } else {
            this._unitPriceEl.textContent = '';
            this._unitPriceEl.hidden = true;
          }
        }

        const savingsEl = this.querySelector('[data-bb-savings-text]');
        if (savingsEl) savingsEl.textContent = `YOU SAVED ${this._formatMoney(totalSavingsCents)}`;

        if (this._footerTitleEl) {
          let titleText = 'BUNDLE DISCOUNT ';
          if (reachedTargetQty) {
            titleText = 'BUNDLE DISCOUNT UNLOCKED ';
          }
          this._footerTitleEl.childNodes[0].textContent = titleText;
        }

        if (this._footerBadgeEl) {
          const ruleLabel = this._formatRuleBenefitLabel(activeRule);
          if (ruleLabel) {
            this._footerBadgeEl.hidden = false;
            this._footerBadgeEl.textContent = ruleLabel;
          } else {
            const tieredPct = this._getTierDiscountPct(total);
            this._footerBadgeEl.hidden = tieredPct === 0;
            if (tieredPct > 0) this._footerBadgeEl.textContent = `${tieredPct}% OFF`;
          }
        }

        if (this._footerSubtitleEl) {
          if (unlockQty > 0 && !reachedTargetQty) {
            const remainingToUnlock = Math.max(unlockQty - total, 0);
            this._footerSubtitleEl.textContent = `ADD ${remainingToUnlock} TO UNLOCK BUNDLE PRICE`;
          } else if (reachedTargetQty) {
            this._footerSubtitleEl.textContent = 'BUNDLE PRICE UNLOCKED';
          } else {
            this._footerSubtitleEl.textContent = 'SELECT FLAVORS TO BUILD YOUR BUNDLE';
          }

          const shouldHideSubtitle = unlockQty > 0 && reachedTargetQty;
          this._footerSubtitleEl.hidden = shouldHideSubtitle;
          this._footerSubtitleEl.classList.toggle('bb-footer__subtitle--hidden', shouldHideSubtitle);
          this._footerSubtitleEl.style.display = shouldHideSubtitle ? 'none' : '';
        }

        if (this._footerFsMessageEl) {
          this._renderFooterNextTierMessage(total);
        }

        if (this._footerPricesEl) {
          const shouldHidePrices = targetQty > 0 && !reachedTargetQty;
          this._footerPricesEl.hidden = shouldHidePrices;
          this._footerPricesEl.classList.toggle('bb-footer__prices--hidden', shouldHidePrices);
          this._footerPricesEl.style.display = shouldHidePrices ? 'none' : '';

          if (this._savingsTagEl) {
            this._savingsTagEl.hidden = shouldHidePrices;
            this._savingsTagEl.style.display = shouldHidePrices ? 'none' : '';
          }
        }
      } else {
        this._footerEl.classList.remove('bb-footer--visible');

        if (this._footerTitleEl) {
          this._footerTitleEl.childNodes[0].textContent = 'BUNDLE DISCOUNT ';
        }

        if (this._footerBadgeEl) {
          this._footerBadgeEl.hidden = true;
          this._footerBadgeEl.textContent = '';
        }

        if (this._footerSubtitleEl) {
          if (unlockQty > 0) {
            this._footerSubtitleEl.textContent = `ADD ${unlockQty} TO UNLOCK BUNDLE PRICE`;
          } else {
            this._footerSubtitleEl.textContent = 'SELECT FLAVORS TO BUILD YOUR BUNDLE';
          }

          this._footerSubtitleEl.hidden = false;
          this._footerSubtitleEl.classList.remove('bb-footer__subtitle--hidden');
          this._footerSubtitleEl.style.display = '';
        }

        if (this._footerFsMessageEl) {
          const fsThr = this._getFreeShippingThreshold();
          const fsRule = fsThr !== null ? this._bundleDiscountRules.find((r) => r.freeShipping && r.minimum === fsThr) : null;
          const fsReached = fsThr !== null && total >= fsThr;
          if (fsRule && !fsReached) {
            const n = fsThr - total;
            let fsLabel = '';
            if (fsRule.discountType === 'fixed_amount') {
              const cents = Math.round(fsRule.value * 100);
              if (cents > 0) fsLabel = `${this._formatMoney(cents)} OFF`;
            } else if (fsRule.value > 0) {
              fsLabel = `${Math.round(fsRule.value)}% OFF`;
            }
            if (fsLabel) {
              this._footerFsMessageEl.textContent = `ADD ${n} ${n === 1 ? 'CAN' : 'CANS'} AND UNLOCK ${fsLabel}`;
              this._footerFsMessageEl.classList.remove('bb-status__tag--hidden');
            } else {
              this._footerFsMessageEl.classList.add('bb-status__tag--hidden');
            }
          } else {
            this._footerFsMessageEl.classList.add('bb-status__tag--hidden');
          }
        }

        if (this._footerPricesEl) {
          this._footerPricesEl.hidden = true;
          this._footerPricesEl.classList.add('bb-footer__prices--hidden');
          this._footerPricesEl.style.display = 'none';
        }

        if (this._savingsTagEl) {
          this._savingsTagEl.hidden = true;
          this._savingsTagEl.style.display = 'none';
        }
      }
    }

    // CTA button
    if (!this._addToCartBtn) return;

    if (total === 0) {
      this._addToCartBtn.textContent = minRequired > 0 ? `ADD AT LEAST ${minRequired} TO CONTINUE` : 'SELECT YOUR FLAVORS';
      this._addToCartBtn.dataset.ready = 'false';
    } else if (!reachedMin) {
      this._addToCartBtn.textContent = `ADD ${remainingMin} MORE TO CONTINUE`;
      this._addToCartBtn.dataset.ready = 'false';
    } else if (maxAllowed !== null && !reachedMax) {
      this._addToCartBtn.textContent = `ADD TO CART (${total}/${maxAllowed})`;
      this._addToCartBtn.dataset.ready = isReady ? 'true' : 'false';
    } else {
      this._addToCartBtn.textContent = 'ADD TO CART';
      this._addToCartBtn.dataset.ready = isReady ? 'true' : 'false';
    }

    if (this._addSubscriptionBtn) {
      if (total === 0) {
        this._addSubscriptionBtn.textContent = minRequired > 0 ? `ADD AT LEAST ${minRequired} TO CONTINUE` : 'SELECT FLAVORS TO SUBSCRIBE';
        this._addSubscriptionBtn.dataset.ready = 'false';
      } else if (!reachedMin) {
        this._addSubscriptionBtn.textContent = `ADD ${remainingMin} MORE TO CONTINUE`;
        this._addSubscriptionBtn.dataset.ready = 'false';
      } else if (maxAllowed !== null && !reachedMax) {
        this._addSubscriptionBtn.textContent = `ADD SUBSCRIPTION (${total}/${maxAllowed})`;
        this._addSubscriptionBtn.dataset.ready = hasSubscriptionPlans && isReady ? 'true' : 'false';
      } else {
        this._addSubscriptionBtn.textContent = 'ADD SUBSCRIPTION TO CART';
        this._addSubscriptionBtn.dataset.ready = hasSubscriptionPlans && isReady ? 'true' : 'false';
      }

      this._addSubscriptionBtn.hidden = !hasSubscriptionPlans;
    }

    if (this._bundleSubscriptionSelect) {
      this._bundleSubscriptionSelect.disabled = !hasSubscriptionPlans;
    }
  }

  // ── Single mode render ────────────────────────────────────

  _renderSingle() {
    if (this._singleQtyEl) {
      this._singleQtyEl.textContent = this._singleQty;
    }
    if (this._singleMinusBtn) {
      this._singleMinusBtn.disabled = this._singleQty <= 1;
    }
    if (this._singlePriceDisplay && this._singlePrice) {
      this._singlePriceDisplay.textContent = this._formatMoney(
        this._singlePrice * this._singleQty
      );
    }
  }

  async _getActiveBundleProperties() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
      const items = Array.isArray(cart?.items) ? cart.items : [];

      const bundleCandidates = items.filter((item) => {
        const bundleId = item?.properties?._bb_bundle_id;
        return typeof bundleId === 'string' && bundleId.trim() !== '';
      });

      if (!bundleCandidates.length) {
        return null;
      }

      const bundleIds = new Set(
        bundleCandidates
          .map((item) => String(item?.properties?._bb_bundle_id || '').trim())
          .filter(Boolean)
      );

      if (bundleIds.size !== 1) {
        return null;
      }

      const activeBundleProperties = bundleCandidates[0]?.properties || {};

      const normalizedBundleProperties = Object.entries(activeBundleProperties).reduce((accumulator, [key, value]) => {
        if (!key.startsWith('_bb_')) {
          return accumulator;
        }

        if (value === null || value === undefined) {
          return accumulator;
        }

        accumulator[key] = String(value);
        return accumulator;
      }, {});

      return Object.keys(normalizedBundleProperties).length ? normalizedBundleProperties : null;
    } catch (error) {
      console.error('[BundleBuilder] Failed to resolve active bundle metadata', error);
      return null;
    }
  }

  // ── Add to cart: bundle (variety) ─────────────────────────

  async _addBundleToCart(useSubscription = false) {
    const button = useSubscription ? this._addSubscriptionBtn : this._addToCartBtn;
    if (!button || button.dataset.ready !== 'true') return;

    const sellingPlanId = useSubscription ? this._getSelectedBundleSellingPlanId() : null;
    if (useSubscription && !sellingPlanId) return;

    const items = [];
    const activeBundleProperties = await this._getActiveBundleProperties();
    const bundleGroupId = activeBundleProperties?._bb_bundle_id || `bb_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const baseBundleProperties = activeBundleProperties || {
      _bb_bundle_id: bundleGroupId,
      _bb_bundle_size: String(this._totalQty),
      _bb_bundle_min_qty: String(this._bundleMinQty),
      _bb_bundle_source: 'bundle-builder',
      _bb_parent_title: this._parentTitle,
      _bb_parent_image: this._parentImage,
    };

    if (this._parentVariantId && !baseBundleProperties._bb_parent_variant_id) {
      baseBundleProperties._bb_parent_variant_id = this._parentVariantId;
    }

    this._items.forEach(({ qty }, variantId) => {
      if (qty > 0) {
        /** @type {{ id: number, quantity: number, properties: Record<string, string>, selling_plan?: number }} */
        const lineItem = {
          id: parseInt(variantId, 10),
          quantity: qty,
          properties: {
            ...baseBundleProperties,
          },
        };

        if (sellingPlanId) {
          lineItem.selling_plan = sellingPlanId;
        }

        items.push(lineItem);
      }
    });
    if (!items.length) return;

    await this._doAddToCart(items, button, () => this._renderVariety());
  }

  // ── Add to cart: single ───────────────────────────────────

  async _addSingleToCart() {
    if (!this._singleVariantId) return;
    const items = [{ id: parseInt(this._singleVariantId, 10), quantity: this._singleQty }];
    await this._doAddToCart(items, this._singleAddBtn, () => this._renderSingle());
  }

  // ── Shared cart fetch ─────────────────────────────────────

  async _doAddToCart(items, btn, onReset) {
    const originalText = btn.textContent;
    btn.dataset.loading = 'true';
    btn.textContent = 'ADDING…';

    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        throw new Error(err.description || 'Cart error');
      }

      // Refresh cart drawer section HTML
      const cartItemsEl = /** @type {HTMLElement|null} */ (
        document.querySelector('cart-items-component[data-section-id]')
      );
      const sectionId = cartItemsEl ? cartItemsEl.dataset.sectionId : null;
      const cartUrl = sectionId ? `/cart?sections=${sectionId}` : '/cart.js';
      const cartData = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );

      if (sectionId && cartData[sectionId] && cartItemsEl) {
        const parser = new DOMParser();
        const freshDoc = parser.parseFromString(cartData[sectionId], 'text/html');
        const freshEl = freshDoc.querySelector('cart-items-component');
        if (freshEl) cartItemsEl.innerHTML = freshEl.innerHTML;
      }

      // Notify theme cart
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartData,
            sourceId: 'bundle-builder',
            data: { itemCount: cartData.item_count },
          },
        })
      );

      // Open drawer
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();

      btn.textContent = '✓ ADDED TO CART!';

      setTimeout(() => {
        btn.dataset.loading = 'false';
        btn.textContent = originalText;
        if (onReset) onReset();
      }, 2000);
    } catch (err) {
      console.error('[BundleBuilder]', err);
      btn.textContent = 'ERROR — TRY AGAIN';
      btn.dataset.loading = 'false';
    }
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
